import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  createLevelAuthConfig,
  disconnectLevelIntegration,
  getLevelAuthConfig,
  getLevelCustomers,
  getLevelDevices,
  getLevelMappedCustomers,
  getLevelMappedDevices,
  mapLevelCustomers,
  mapLevelDevices,
  updateLevelAuthConfig,
  updateLevelStatus,
} from "controller/api";
import { ApiErrorType } from "shared";
import {
  MapLevelCustomersInput,
  MapLevelDevicesInput,
  UpdateLevelAuthConfigInput,
} from "shared/types/integrations";
import { sentryLogger } from "utilz";

export const useCreateLevelAuthConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (api_key: string) => createLevelAuthConfig(api_key),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["levelAuthConfig"],
      });
      queryClient.invalidateQueries({
        queryKey: ["integrationSources"],
      });
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "integration",
        functionName: "createLevelAuthConfig",
      });
    },
  });
};

export const useUpdateLevelAuthConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateLevelAuthConfigInput) =>
      updateLevelAuthConfig(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["levelAuthConfig"],
      });
      queryClient.invalidateQueries({
        queryKey: ["integrationSources"],
      });
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "integration",
        functionName: "updateLevelAuthConfig",
      });
    },
  });
};

export const useGetLevelAuthConfig = () => {
  return useQuery({
    queryKey: ["levelAuthConfig"],
    queryFn: () => getLevelAuthConfig(),
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "integration",
        functionName: "getLevelAuthConfig",
      });
    },
    // staleTime: Infinity,
  });
};

export const useGetLevelCustomers = (perPage: number = 20) => {
  return useInfiniteQuery({
    queryKey: ["levelCustomers", perPage],
    queryFn: ({ pageParam = "" }) =>
      getLevelCustomers({
        perPage,
        startingAfter: pageParam,
      }),
    getNextPageParam: (lastPage) => {
      return lastPage?.data?.length > 0 ? lastPage?.lastItemId : undefined;
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "integration",
        functionName: "getLevelCustomers",
      });
    },
    // staleTime: Infinity,
  });
};

export const useGetLevelDevices = (perPage: number = 20) => {
  return useInfiniteQuery({
    queryKey: ["levelDevices", perPage],
    queryFn: ({ pageParam = "" }) =>
      getLevelDevices({ perPage, startingAfter: pageParam }),
    getNextPageParam: (lastPage) => {
      return lastPage?.data?.length > 0 ? lastPage?.lastItemId : undefined;
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "integration",
        functionName: "getLevelDevices",
      });
    },
    // staleTime: Infinity,
  });
};

export const useMapLevelCustomers = () => {
  return useMutation({
    mutationFn: (data: MapLevelCustomersInput) => mapLevelCustomers(data),
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "integration",
        functionName: "mapLevelCustomers",
      });
    },
  });
};

export const useGetLevelMappedCustomers = () => {
  return useQuery({
    queryKey: ["levelMappedCustomers"],
    queryFn: () => getLevelMappedCustomers(),
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "integration",
        functionName: "getLevelMappedCustomers",
      });
    },
  });
};

export const useMapLevelDevices = () => {
  return useMutation({
    mutationFn: (data: MapLevelDevicesInput) => mapLevelDevices(data),
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "integration",
        functionName: "mapLevelDevices",
      });
    },
  });
};

export const useGetLevelMappedDevices = () => {
  return useQuery({
    queryKey: ["levelMappedDevices"],
    queryFn: () => getLevelMappedDevices(),
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "integration",
        functionName: "getLevelMappedDevices",
      });
    },
  });
};

export const useDisconnectLevelIntegration = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => disconnectLevelIntegration(),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: ["integrationSources"],
      });
      queryClient.invalidateQueries({
        queryKey: ["levelAuthConfig"],
      });
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "integration",
        functionName: "disconnectLevelIntegration",
      });
    },
  });
};

export const useUpdateLevelStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (status: boolean) => updateLevelStatus(status),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(["integrationSources"]);
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "integration",
        functionName: "updateLevelStatus",
      });
    },
  });
};
